import Clock from "images/product/clock.svg";
import find from "lodash/find";
import orderBy from "lodash/orderBy";
import uniqBy from "lodash/uniqBy";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect } from "react";

import { useFunnelContext } from "shared/Funnel/FunnelContextProvider";
import { TrustpilotWidget } from "shared/TrustpilotWidget";
import { useTrackProduct } from "shared/analytics/useTrackProduct";
import { ConditionOffer } from "shared/product/ProductPageContext";
import { useProductPageContext } from "shared/product/ProductPageContext/useProductPageContext";
import { ProductImages } from "shared/product/ProductSection/ProductImages";
import { HorizontalSeparator } from "ui/HorizontalSeparator";
import { StrapiImageData } from "ui/StrapiImage";
import { useI18n } from "utils/with-i18n.utils";

import { Variant } from "../../product-section.helper";
import { PictureThumbnail } from "../shared/PictureThumbnail";
import { StorageCard } from "../shared/StorageCard";

import { AdvantageDetails } from "./AdvantageDetails";
import { CommitmentDetails } from "./CommitmentDetails";
import { Condition, DeviceCondition } from "./DeviceConditionSelector";
import { StickySection } from "./StickySection";

export const AvailableProduct = () => {
  const i18n = useI18n();
  const { isSwap } = useFunnelContext();
  const {
    family,
    brandNewVariants,
    refurbishedVariants,
    currentVariant,
    setCurrentVariant,
    selectedOfferCondition,
    setSelectedOfferCondition,
    isPreOrder,
    deliveryDate,
  } = useProductPageContext();
  const {
    query: { slug },
  } = useRouter();

  const { trackProductClicked, trackProductViewed } = useTrackProduct();

  const mustBePurchasedSelected = selectedOfferCondition === ConditionOffer.NEW;

  useEffect(() => {
    if (currentVariant?.sku) {
      trackProductViewed(currentVariant.sku);
    }
  }, [currentVariant, trackProductViewed]);

  const basePath = `${isSwap ? "/swap" : ""}/smartphone/${slug}`;
  const subscriptionSku = currentVariant?.sku;

  const selectedConditionOfferVariants = mustBePurchasedSelected
    ? brandNewVariants
    : refurbishedVariants;

  const conditions: Condition[] = [
    {
      offer: ConditionOffer.REFURBISHED,
      label: i18n.t("product.conditionOffer.refurbished"),
      variants: refurbishedVariants,
    },
    {
      offer: ConditionOffer.NEW,
      label: i18n.t("product.conditionOffer.new"),
      variants: brandNewVariants,
    },
  ];

  const availableColors = uniqBy(
    selectedConditionOfferVariants?.filter(
      ({ storageSlug }) => storageSlug === currentVariant?.storageSlug,
    ),
    "colorSlug",
  );
  const availableStorages = orderBy(
    uniqBy(
      selectedConditionOfferVariants?.filter(
        ({ colorSlug }) => colorSlug === currentVariant?.colorSlug,
      ),
      "storageSlug",
    ),
    (variant) => parseInt(variant.storageSlug, 10),
    "asc",
  );
  const availableContractTerms = orderBy(
    uniqBy(
      selectedConditionOfferVariants?.filter(
        ({ colorSlug, storageSlug }) =>
          storageSlug === currentVariant?.storageSlug &&
          colorSlug === currentVariant?.colorSlug,
      ),
      "commitmentPeriod",
    ),
    ["commitmentPeriod"],
    ["desc"],
  );

  if (!subscriptionSku) {
    console.error("Missing subscription SKU, ordering is disabled.");
  }

  const isColorInStock = (colorSlug: string) =>
    !!find(selectedConditionOfferVariants, {
      colorSlug,
      storageSlug: currentVariant?.storageSlug,
      isInStock: true,
    });

  const isStorageInStock = (storageSlug: string) =>
    !!find(selectedConditionOfferVariants, {
      storageSlug,
      colorSlug: currentVariant?.colorSlug,
      isInStock: true,
    });

  const isSelectedConditionOfferAvailable =
    (selectedConditionOfferVariants?.length ?? 0) > 0;

  const parsedDeliveryDate = deliveryDate ? new Date(deliveryDate) : undefined;

  return (
    <>
      <div className="flex flex-col gap-4 items-center py-5 px-2 lg:items-start lg:p-8 lg:w-full">
        {/* Title */}
        <h1 className="mt-12 text-2xl text-center lg:mt-0 lg:text-4xl lg:text-left 2xs:text-3xl">
          {i18n.t(
            "product.available.newPhoneName",
            { productName: family.modelName },
            { html: true },
          )}
        </h1>

        {/* Trustpilot Desktop */}
        <TrustpilotWidget className="hidden mb-5 -ml-4 w-[420px] lg:block" />

        {/* Mobile carousel */}
        <ProductImages className="-mt-5 w-screen min-w-320 lg:hidden" />
        <HorizontalSeparator className="w-screen min-w-320 lg:hidden" />

        {/* Pre-order Delivery Date */}
        {isPreOrder && (
          <div className="flex items-center py-2 px-4 mt-2 w-80 text-white bg-black rounded-lg border lg:w-96">
            <Clock />
            <div className="ml-4">
              <p className="font-semibold">{i18n.t("product.preOrder.badge")}</p>
              {parsedDeliveryDate ? (
                <p>
                  {i18n.t("product.preOrder.deliveryFrom")}{" "}
                  {new Intl.DateTimeFormat("fr-FR", {
                    day: "2-digit",
                    month: "2-digit",
                  }).format(parsedDeliveryDate)}
                </p>
              ) : (
                <p>{i18n.t("product.preOrder.deliveryASAP")}</p>
              )}
            </div>
          </div>
        )}

        {/* Device offer condition selector */}
        <p className="text-black">{i18n.t("product.deviceOfferCondition")}</p>
        <div className="flex justify-center w-full lg:justify-start">
          <div className="flex flex-wrap gap-2 justify-center max-w-2xl">
            {conditions.map((condition) => {
              const isConditionAvailable =
                (condition.variants?.length ?? 0) > 0;

              return isConditionAvailable ? (
                <Link
                  key={condition.label}
                  href={`${basePath}?condition=${condition.offer}`}
                  replace
                  shallow
                  scroll={false}
                >
                  <DeviceCondition
                    key={condition.label}
                    condition={condition}
                    isSelected={selectedOfferCondition === condition.offer}
                    onClick={() => setSelectedOfferCondition(condition.offer)}
                  />
                </Link>
              ) : (
                <DeviceCondition
                  key={condition.label}
                  condition={condition}
                  isSelected={selectedOfferCondition === condition.offer}
                />
              );
            })}
          </div>
        </div>

        {/* Color selector */}
        {isSelectedConditionOfferAvailable && (
          <p className="text-black">{i18n.t("product.pickColor")}</p>
        )}
        <div className="flex flex-wrap gap-2 justify-center">
          {availableColors?.map(({ id, sku, colorSlug, backPicture }) => (
            <Link
              key={id}
              href={`${basePath}/${currentVariant?.storageSlug}-${colorSlug}?condition=${selectedOfferCondition}`}
              replace
              shallow
              scroll={false}
            >
              <PictureThumbnail
                image={backPicture as StrapiImageData}
                isSelected={colorSlug === currentVariant?.colorSlug}
                isDisabled={!isColorInStock(colorSlug)}
                onClick={() => !isSwap && trackProductClicked(sku!)}
              />
            </Link>
          ))}
        </div>

        {/* Storage selector */}
        {isSelectedConditionOfferAvailable && (
          <p className="text-black">{i18n.t("product.pickStorage")}</p>
        )}
        <div className="flex flex-wrap gap-2 justify-center">
          {availableStorages.map(
            ({ id, sku, storage, storageSlug: variantStorageSlug }) => (
              <Link
                key={id}
                href={`${basePath}/${variantStorageSlug}-${currentVariant?.colorSlug}?condition=${selectedOfferCondition}`}
                replace
                shallow
                scroll={false}
              >
                <StorageCard
                  label={storage ?? ""}
                  isSelected={
                    variantStorageSlug === currentVariant?.storageSlug
                  }
                  isDisabled={!isStorageInStock(variantStorageSlug)}
                  onClick={() => !isSwap && trackProductClicked(sku!)}
                />
              </Link>
            ),
          )}
        </div>

        {/* Commitment selector */}
        {isSelectedConditionOfferAvailable && (
          <p className="text-black">
            {i18n.t("product.pickCommitment", {}, { html: true })}
          </p>
        )}
        <div className="flex flex-col gap-2 items-center w-full">
          {availableContractTerms.map((contractTerm) => (
            <CommitmentDetails
              key={contractTerm.id}
              contractTerm={contractTerm}
              currentVariant={currentVariant}
              onClick={(variant: Variant) => {
                setCurrentVariant(variant);
              }}
            />
          ))}
        </div>

        {/* Advantages */}
        <AdvantageDetails currentVariant={currentVariant} />

        {/* Trustpilot Mobile */}
        <TrustpilotWidget className="mt-2 lg:hidden" />

        {/* Sticky section for mobile */}
        <StickySection className="fixed bottom-0 w-screen min-w-320 lg:hidden" />
      </div>
    </>
  );
};
